import { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';

/**
 * Organization Lifecycle modal — the plain-language control for retiring a
 * customer organization when a contract ends.
 *
 * The flow reads like a short story, not a control panel:
 *   1. Suspend access   (people can't sign in; nothing is deleted yet)
 *   2. Schedule deletion (a recovery countdown starts; cancel any time)
 *   3. Approve deletion  (a second admin signs off — separation of duties)
 *   4. Delete permanently (only after the countdown, and you type the name)
 *
 * Everything here is additive — it never touches onboarding.
 */
export default function OrgLifecycleModal({ org, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState('');
  const [retentionDays, setRetentionDays] = useState(30);
  const [justification, setJustification] = useState('');
  const [confirmName, setConfirmName] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/v2/orgs/${org.id}/lifecycle`, { credentials: 'include' });
      const json = await res.json();
      if (json.success) {
        setData(json);
        setRetentionDays(json.settings?.purge_retention_days || 30);
      } else {
        setError(json.message || 'Failed to load');
      }
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const act = async (path, body) => {
    setBusy(true);
    setError(null);
    let ok = false;
    try {
      const res = await fetch(`${API}/api/admin/v2/orgs/${org.id}/lifecycle/${path}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      const json = await res.json();
      if (json.success) {
        ok = true;
        setReason('');
        setJustification('');
        setConfirmName('');
        onChanged && onChanged();
        // A permanent deletion removes the org entirely — there's nothing left
        // to reload, so close the modal instead of re-fetching (which 404s).
        if (path === 'purge-now') { onClose(); return true; }
        await load();
      } else {
        setError(json.message || 'Action failed');
      }
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
    return ok;
  };

  const downloadExport = () => {
    window.open(`${API}/api/admin/v2/orgs/${org.id}/lifecycle/export`, '_blank');
  };

  const o = data?.organization;
  const f = data?.flags || {};
  const status = o?.lifecycle_status || 'active';

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/75 backdrop-blur-sm px-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="w-full max-w-2xl bg-slate-900 border border-slate-700/70 rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-700/60 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-bold text-white truncate">{org.name}</h3>
              <StatusPill status={status} />
            </div>
            <p className="text-xs text-slate-400 mt-1">Manage this organization’s access and data when a contract ends.</p>
          </div>
          <button onClick={onClose} disabled={busy} className="text-slate-400 hover:text-white text-2xl leading-none disabled:opacity-40">×</button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto space-y-5">
          {loading ? (
            <p className="text-slate-400 text-sm">Loading…</p>
          ) : (
            <>
              {error && (
                <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>
              )}

              {f.is_protected && (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
                  This is a protected organization. It can be viewed here but never suspended or deleted.
                </div>
              )}

              {/* What's happening now, in plain words */}
              <PlainStatus status={status} flags={f} org={o} />

              {/* ─── ACTIONS depend on the current state ─── */}

              {/* Active → suspend */}
              {status === 'active' && !f.is_protected && (
                <Section title="Step 1 — Suspend access" desc="Everyone in this organization is signed out and can no longer log in. No data is deleted. You can resume access at any time.">
                  <ReasonField value={reason} onChange={setReason} placeholder="Why are you suspending? (e.g. contract ended 2026-06-30, ticket #1234)" />
                  <ActionButton tone="amber" busy={busy} disabled={!reason.trim()} onClick={() => act('suspend', { reason })}>
                    Suspend access
                  </ActionButton>
                </Section>
              )}

              {/* Suspended → resume or schedule deletion */}
              {status === 'suspended' && (
                <>
                  <Section title="Resume access" desc="Turn access back on. Everyone can sign in again immediately.">
                    <ReasonField value={reason} onChange={setReason} placeholder="Optional note" optional />
                    <ActionButton tone="emerald" busy={busy} onClick={() => act('resume', { reason })}>Resume access</ActionButton>
                  </Section>

                  {!f.is_protected && (
                    <Section title="Step 2 — Schedule deletion" desc="Start a recovery countdown. Nothing is deleted during the countdown, and you can cancel any time. When the countdown ends and a second admin has approved, all of this organization’s data is permanently deleted.">
                      <label className="block text-xs text-slate-400 mb-1">Recovery window before permanent deletion</label>
                      <select
                        value={retentionDays}
                        onChange={(e) => setRetentionDays(Number(e.target.value))}
                        className="w-full mb-3 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
                      >
                        <option value={7}>7 days</option>
                        <option value={30}>30 days (recommended)</option>
                        <option value={60}>60 days</option>
                        <option value={90}>90 days</option>
                      </select>
                      <ReasonField value={reason} onChange={setReason} placeholder="Reason for deletion (required) — e.g. contract terminated, data retention expired" />
                      <ActionButton tone="red" busy={busy} disabled={!reason.trim()} onClick={() => act('schedule-purge', { reason, retention_days: retentionDays })}>
                        Schedule deletion
                      </ActionButton>
                    </Section>
                  )}
                </>
              )}

              {/* Pending purge → countdown, approve, cancel, delete-now */}
              {status === 'pending_purge' && (
                <>
                  <Countdown org={o} flags={f} />

                  <Section title="Cancel deletion" desc="Stop the countdown. The organization goes back to suspended (still no access), and nothing is deleted.">
                    <ReasonField value={reason} onChange={setReason} placeholder="Optional note" optional />
                    <ActionButton tone="emerald" busy={busy} onClick={() => act('cancel-purge', { reason })}>Cancel deletion</ActionButton>
                  </Section>

                  {f.needs_approval && (
                    <Section title="Approve deletion" desc="A deletion must be approved before it can run. For separation of duties, this should be a different admin than the one who scheduled it.">
                      {data?.settings?.require_separate_purge_approver && (
                        <p className="text-xs text-amber-300 mb-2">
                          If you scheduled this deletion yourself, you must write a justification below to approve it (recorded as a self-approval).
                        </p>
                      )}
                      <ReasonField value={justification} onChange={setJustification} placeholder="Justification (needed only if you are approving your own request)" optional />
                      <ActionButton tone="amber" busy={busy} onClick={() => act('approve-purge', { self_approval_justification: justification })}>
                        Approve deletion
                      </ActionButton>
                    </Section>
                  )}

                  {!f.needs_approval && (
                    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
                      Approved{o.purge_self_approved ? ' (self-approved with justification)' : ''}.
                      {f.window_elapsed ? ' The recovery window has ended — permanent deletion is now available below.' : ' It will delete automatically when the recovery window ends.'}
                    </div>
                  )}

                  {f.can_execute_purge && (
                    <Section title="Delete permanently now" desc="This cannot be undone. Every record belonging to this organization will be permanently erased. A record of the deletion is kept for audit." danger>
                      <label className="block text-xs text-slate-400 mb-1">Type the organization name to confirm: <span className="text-white font-semibold">{o.name}</span></label>
                      <input
                        value={confirmName}
                        onChange={(e) => setConfirmName(e.target.value)}
                        placeholder={o.name}
                        className="w-full mb-3 bg-slate-800 border border-red-700/50 rounded-lg px-3 py-2 text-sm text-white"
                      />
                      <ActionButton tone="red" busy={busy} disabled={confirmName.trim() !== o.name} onClick={() => act('purge-now', { confirm_name: confirmName, reason })}>
                        Delete permanently
                      </ActionButton>
                    </Section>
                  )}
                </>
              )}

              {/* Data export — always available */}
              <Section title="Download a copy of all data" desc="Export everything this organization owns as a single JSON file. Do this before deleting so the customer keeps their records.">
                <button onClick={downloadExport} disabled={busy} className="px-4 py-2 rounded-lg bg-slate-700/60 hover:bg-slate-700 border border-slate-600 text-sm text-white transition disabled:opacity-50">
                  ↓ Download data export
                </button>
              </Section>

              {/* History */}
              {data?.events?.length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-2">History</h4>
                  <ul className="space-y-2">
                    {data.events.map((ev) => (
                      <li key={ev.id} className="text-xs text-slate-400 flex gap-3">
                        <span className="text-slate-500 whitespace-nowrap">{new Date(ev.created_at).toLocaleString()}</span>
                        <span className="text-slate-200 font-medium">{labelForEvent(ev.event)}</span>
                        <span className="text-slate-500 truncate">{ev.actor_email}{ev.reason ? ` · ${ev.reason}` : ''}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active: ['Active', 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'],
    suspended: ['Suspended', 'bg-amber-500/15 text-amber-300 border-amber-500/40'],
    pending_purge: ['Scheduled for deletion', 'bg-red-500/15 text-red-300 border-red-500/40'],
  };
  const [label, cls] = map[status] || map.active;
  return <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${cls}`}>{label}</span>;
}

function PlainStatus({ status, flags, org }) {
  let text;
  if (status === 'active') text = 'This organization is live. People can sign in and use the platform normally.';
  else if (status === 'suspended') text = 'Access is turned off. No one in this organization can sign in, but all of their data is safe. Nothing has been deleted.';
  else if (status === 'pending_purge') {
    text = flags.window_elapsed
      ? 'The recovery window has ended. This organization is ready to be permanently deleted.'
      : `Permanent deletion is scheduled. There are ${flags.days_remaining} day(s) left to cancel.`;
  }
  return <p className="text-sm text-slate-300 leading-relaxed">{text}</p>;
}

function Countdown({ org, flags }) {
  return (
    <div className="rounded-xl border border-red-500/30 bg-red-500/5 px-5 py-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-400">Permanent deletion scheduled for</p>
          <p className="text-lg font-bold text-white">{org.purge_scheduled_at ? new Date(org.purge_scheduled_at).toLocaleString() : '—'}</p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold text-red-300">{flags.days_remaining ?? '—'}</p>
          <p className="text-xs text-slate-400">day(s) to cancel</p>
        </div>
      </div>
      {org.purge_reason && <p className="text-xs text-slate-400 mt-3">Reason: {org.purge_reason}</p>}
    </div>
  );
}

function Section({ title, desc, children, danger }) {
  return (
    <div className={`rounded-xl border px-4 py-4 ${danger ? 'border-red-700/50 bg-red-950/20' : 'border-slate-700/50 bg-slate-800/30'}`}>
      <p className="text-sm font-semibold text-white">{title}</p>
      {desc && <p className="text-xs text-slate-400 mt-1 mb-3 leading-relaxed">{desc}</p>}
      {children}
    </div>
  );
}

function ReasonField({ value, onChange, placeholder, optional }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={2}
      className="w-full mb-3 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 resize-none"
    />
  );
}

function ActionButton({ tone, busy, disabled, onClick, children }) {
  const tones = {
    amber: 'bg-amber-500/20 hover:bg-amber-500/30 border-amber-500/40 text-amber-200',
    emerald: 'bg-emerald-500/20 hover:bg-emerald-500/30 border-emerald-500/40 text-emerald-200',
    red: 'bg-red-600/30 hover:bg-red-600/50 border-red-500/50 text-red-100',
  };
  return (
    <button
      onClick={onClick}
      disabled={busy || disabled}
      className={`px-4 py-2 rounded-lg border text-sm font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed ${tones[tone] || tones.amber}`}
    >
      {busy ? 'Working…' : children}
    </button>
  );
}

function labelForEvent(ev) {
  return {
    suspend: 'Access suspended',
    resume: 'Access resumed',
    schedule_purge: 'Deletion scheduled',
    cancel_purge: 'Deletion cancelled',
    approve_purge: 'Deletion approved',
    purge_executed: 'Permanently deleted',
    data_export: 'Data exported',
    settings_change: 'Settings changed',
  }[ev] || ev;
}
